import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import { isGoldVerified, GoldVerifiedBadge } from './Profile';
import CollaborationModal from '../components/CollaborationModal';
import ExcelRoomUploadModal from '../components/ExcelRoomUploadModal';
import AttendanceAdmin from './faculty/AttendanceAdmin';
import FeedbackDashboard from '../components/FeedbackDashboard';
import './Home.css';
import { Home, LogOut, RefreshCw, Send, AlertCircle, CheckCircle, Clock, Mail, RotateCcw, Radio, Users, Briefcase, Activity, Calendar, MessageSquare, Search, ChevronLeft, ChevronRight, BarChart2, UserCheck, FileSpreadsheet, Sparkles, FlaskConical, UserPlus, Trash2, Power } from 'lucide-react';

// Admin emails (normalize leading @ if present)
const ADMIN_EMAIL = "24071a6691@vnrvjiet.in";
const CO_ADMIN_EMAIL = "24071a6679@vnrvjiet.in";
const TEST_ADMIN_EMAIL = "24071a6683@vnrvjiet.in";

function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [currentTab, setCurrentTab] = useState('analytics'); // 'analytics', 'students', 'faculty', 'formation', 'explore', or 'feedback'
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loginStats, setLoginStats] = useState({ stats: { day: 0, week: 0, month: 0 }, users: [] });
  const [statsDate, setStatsDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [statsDetailTitle, setStatsDetailTitle] = useState("");
  const [formationTasks, setFormationTasks] = useState([]);
  const [closedTasks, setClosedTasks] = useState([]);
  const [formationSubTab, setFormationSubTab] = useState('active'); // 'active' | 'past'
  const [zipProgress, setZipProgress] = useState(null); // { taskId: string, logs: string[] }
  const zipLogsEndRef = useRef(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newTask, setNewTask] = useState({ name: "", max_members: 6, sectionLimits: {}, genderRequirements: { female: 2 }, expires_at: "" });
  const [editingTask, setEditingTask] = useState(null);
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);
  const [selectedTaskForCollaboration, setSelectedTaskForCollaboration] = useState(null);
  const [user, setUser] = useState(null);

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [isAllowed, setIsAllowed] = useState(false);
  const pollingRef = useRef(null);

  // Attendance & Email Logs state
  const [attSummary, setAttSummary] = useState({ coordinator_pending: 0, hod_pending: 0, faculty_pending: 0, completed: 0, rejected: 0, today_total: 0 });
  const [attRequests, setAttRequests] = useState([]);
  const [attFilter, setAttFilter] = useState({ status: '', date: '', department: '' });
  const [attLoading, setAttLoading] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({ total: { sent: 0, failed: 0 }, today: { sent: 0, failed: 0 }, todayByType: [] });
  const [emailFilter, setEmailFilter] = useState({ type: '', status: '', date: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [retriggerLoading, setRetriggerLoading] = useState({});

  // Broadcast state
  const [broadcastLogs, setBroadcastLogs] = useState([]);
  const [broadcastStats, setBroadcastStats] = useState({ totalBroadcasts: 0, todayBroadcasts: 0, totalRecipients: 0, todayRecipients: 0 });
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastAudience, setBroadcastAudience] = useState('all');
  const [expandedBroadcast, setExpandedBroadcast] = useState(null);

  // Events & Networking state
  const [adminEvents, setAdminEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventStats, setEventStats] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', slug: '', description: '', banner: '', start_date: '', end_date: '', venue: '', mode: 'In-Person', organization: 'VNRVJIET' });
  const [assignAdminEmail, setAssignAdminEmail] = useState('');
  const [showCreateAct, setShowCreateAct] = useState(false);
  const [newAct, setNewAct] = useState({ name: '', category: 'Hackathon', description: '', rules: '', date: '', start_time: '09:00', end_time: '18:00', venue: '', team_size: 4, registration_limit: 100 });
  const [eventAnnouncement, setEventAnnouncement] = useState({ title: '', message: '' });
  
  // Admin connections modal state
  const [adminConnections, setAdminConnections] = useState([]);
  const [showAdminConnectionsModal, setShowAdminConnectionsModal] = useState(false);
  const [adminConnectionsSearch, setAdminConnectionsSearch] = useState('');
  const [loadingAdminConnections, setLoadingAdminConnections] = useState(false);

  // Admin participants state
  const [adminParticipants, setAdminParticipants] = useState([]);
  const [adminParticipantsSearch, setAdminParticipantsSearch] = useState('');
  const [loadingAdminParticipants, setLoadingAdminParticipants] = useState(false);

  // Test Faculty Logins state
  const [testLoginsEnabled, setTestLoginsEnabled] = useState(true);
  const [testLoginsList, setTestLoginsList] = useState([]);
  const [testLoginsLoading, setTestLoginsLoading] = useState(false);
  const [testLoginsSearch, setTestLoginsSearch] = useState('');
  const [studentRollInput, setStudentRollInput] = useState('');
  const [studentSearchResults, setStudentSearchResults] = useState([]);
  const [addTestLoginLoading, setAddTestLoginLoading] = useState(false);
  const [testLoginsMessage, setTestLoginsMessage] = useState(null);

  const fetchTestLoginsStatus = useCallback(async (isInitial = false) => {
    if (isInitial) setTestLoginsLoading(true);
    try {
      const res = await fetch(`${API_URL}/test-logins/status`);
      if (res.ok) {
        const data = await res.json();
        setTestLoginsEnabled(data.enabled);
        setTestLoginsList(data.list || []);
      }
    } catch (e) {
      console.warn('Failed to fetch test faculty logins', e);
    } finally {
      setTestLoginsLoading(false);
    }
  }, []);

  const handleToggleGlobalTestLogins = async () => {
    try {
      const nextState = !testLoginsEnabled;
      const res = await fetch(`${API_URL}/test-logins/toggle-global`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: nextState })
      });
      if (res.ok) {
        const data = await res.json();
        setTestLoginsEnabled(data.enabled);
        setTestLoginsMessage({
          type: data.enabled ? 'success' : 'warning',
          text: data.enabled ? 'Test Faculty Logins ENABLED! Active test students will now authenticate as Faculty.' : 'Test Faculty Logins DISABLED! All student accounts strictly authenticate as Students.'
        });
        setTimeout(() => setTestLoginsMessage(null), 5000);
      }
    } catch (e) {
      console.error("Failed to toggle global test logins:", e);
      alert("Error toggling test logins");
    }
  };

  const handleToggleUserTestLogin = async (id, currentActive) => {
    try {
      const res = await fetch(`${API_URL}/test-logins/toggle-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, is_active: !currentActive })
      });
      if (res.ok) {
        setTestLoginsList(prev => prev.map(item => item.id === id ? { ...item, is_active: !currentActive } : item));
      }
    } catch (e) {
      console.error("Failed to toggle user test login:", e);
    }
  };

  const handleRemoveTestLogin = async (id, rollNo) => {
    if (!window.confirm(`Remove ${rollNo} from faculty test logins? They will revert strictly to student login.`)) return;
    try {
      const res = await fetch(`${API_URL}/test-logins/remove/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setTestLoginsList(prev => prev.filter(item => item.id !== id));
        setTestLoginsMessage({ type: 'info', text: `Removed ${rollNo} from faculty test logins.` });
        setTimeout(() => setTestLoginsMessage(null), 4000);
      }
    } catch (e) {
      console.error("Failed to remove test login:", e);
    }
  };

  const handleStudentRollSearch = (query) => {
    setStudentRollInput(query);
    if (!query || query.trim().length < 2) {
      setStudentSearchResults([]);
      return;
    }
    const q = query.trim().toLowerCase();
    const matched = users.filter(u => 
      (u.rollNo && u.rollNo.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.name && u.name.toLowerCase().includes(q))
    ).slice(0, 8);
    setStudentSearchResults(matched);
  };

  const handleAddTestLogin = async (studentObj = null) => {
    const roll = (studentObj?.rollNo || studentRollInput).trim();
    if (!roll && !studentObj?.email) {
      alert("Please enter or select a student roll number");
      return;
    }

    setAddTestLoginLoading(true);
    try {
      const res = await fetch(`${API_URL}/test-logins/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roll_no: roll,
          email: studentObj?.email,
          name: studentObj?.name,
          department: studentObj?.department
        })
      });
      if (res.ok) {
        const data = await res.json();
        setStudentRollInput('');
        setStudentSearchResults([]);
        fetchTestLoginsStatus();
        setTestLoginsMessage({
          type: 'success',
          text: `Added ${data.item?.roll_no || roll} to active test logins!`
        });
        setTimeout(() => setTestLoginsMessage(null), 4000);
      } else {
        const err = await res.json();
        alert(err.error || "Failed to add student to test logins");
      }
    } catch (e) {
      console.error("Failed to add test login:", e);
      alert("Error adding student to test logins");
    } finally {
      setAddTestLoginLoading(false);
    }
  };

  const fetchAdminConnections = useCallback(async (eventId = selectedEventId) => {
    const targetId = eventId || selectedEventId;
    if (!targetId || !user?.email) return;
    setLoadingAdminConnections(true);
    try {
      const res = await fetch(`${API_URL}/events-admin/${targetId}/connections?admin_email=${user.email}`);
      if (res.ok) {
        const data = await res.json();
        setAdminConnections(data.connections || []);
      }
    } catch (e) {
      console.warn('Failed to fetch admin connections', e);
    } finally {
      setLoadingAdminConnections(false);
    }
  }, [selectedEventId, user]);

  const fetchAdminEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/events-admin/my-events?email=${user?.email || ''}`);
      if (res.ok) {
        const data = await res.json();
        const evs = data.events || [];
        setAdminEvents(evs);
        if (evs.length > 0) {
          setSelectedEventId(prev => {
            const exists = evs.some(e => String(e.id) === String(prev));
            return exists ? prev : String(evs[0].id);
          });
        }
      }
    } catch (e) { console.warn('Failed to fetch admin events', e); }
  }, [user]);

  const fetchEventStats = useCallback(async (eventId = selectedEventId) => {
    const targetId = eventId || selectedEventId;
    if (!targetId || !user?.email) return;
    try {
      const res = await fetch(`${API_URL}/events-admin/${targetId}/dashboard?admin_email=${user.email}`);
      if (res.ok) {
        const data = await res.json();
        setEventStats(data);
      }
    } catch (e) { console.warn('Failed to fetch event stats', e); }
  }, [selectedEventId, user]);

  const fetchAdminParticipants = useCallback(async (eventId = selectedEventId) => {
    const targetId = eventId || selectedEventId;
    if (!targetId || !user?.email) return;
    setLoadingAdminParticipants(true);
    try {
      const res = await fetch(`${API_URL}/events-admin/${targetId}/participants?admin_email=${user.email}&limit=100`);
      if (res.ok) {
        const data = await res.json();
        setAdminParticipants(data.participants || []);
      }
    } catch (e) {
      console.warn('Failed to fetch admin participants', e);
    } finally {
      setLoadingAdminParticipants(false);
    }
  }, [selectedEventId, user]);

  const fetchLoginStats = useCallback(async (selectedDate) => {
    setLoadingStats(true);
    try {
      const res = await fetch(`${API_URL}/auth/admin/stats/logins?requester_email=${user?.email || ''}&date=${selectedDate || ''}`);
      if (res.ok) {
        const data = await res.json();
        setLoginStats(data);
      }
    } catch (e) {
      console.warn("Failed to fetch login stats", e);
    } finally {
      setLoadingStats(false);
    }
  }, [user]);

  useEffect(() => {
    if (currentTab === 'analytics' && user?.email) {
      fetchLoginStats(statsDate);
    }
  }, [currentTab, statsDate, user, fetchLoginStats]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .admin-layout {
        display: flex;
        min-height: 100vh;
        background: linear-gradient(135deg, #0b0c16 0%, #151632 50%, #201736 100%);
        transition: background 0.3s ease;
      }
      [data-theme="light"] .admin-layout {
        background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #cbd5e1 100%);
      }
      .admin-sidebar {
        width: 280px;
        background: rgba(255, 255, 255, 0.03);
        backdrop-filter: blur(20px) saturate(120%);
        border-right: 1px solid rgba(255, 255, 255, 0.08);
        display: flex;
        flex-direction: column;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        z-index: 1000;
        position: relative;
        flex-shrink: 0;
      }
      [data-theme="light"] .admin-sidebar {
        background: rgba(255, 255, 255, 0.5);
        border-right: 1px solid rgba(15, 23, 42, 0.08);
      }
      .admin-sidebar.collapsed {
        width: 80px;
      }
      .admin-sidebar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 24px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      }
      [data-theme="light"] .admin-sidebar-header {
        border-bottom: 1px solid rgba(15, 23, 42, 0.05);
      }
      .admin-sidebar-nav {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 16px;
        overflow-y: auto;
      }
      .admin-nav-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-radius: 12px;
        color: rgba(255, 255, 255, 0.7);
        background: transparent;
        border: none;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.2s ease;
        text-align: left;
        width: 100%;
        text-transform: capitalize;
      }
      [data-theme="light"] .admin-nav-item {
        color: #475569;
      }
      .admin-nav-item:hover {
        background: rgba(255, 255, 255, 0.05);
        color: white;
        transform: translateX(4px);
      }
      [data-theme="light"] .admin-nav-item:hover {
        background: rgba(15, 23, 42, 0.04);
        color: #0f172a;
      }
      .admin-nav-item.active {
        background: var(--accent-color, #6B4EE6);
        color: white;
        box-shadow: 0 4px 20px rgba(107, 78, 230, 0.35);
      }
      .admin-main {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .admin-topbar {
        height: 70px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 32px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(10px);
        background: rgba(15, 16, 32, 0.2);
      }
      [data-theme="light"] .admin-topbar {
        border-bottom: 1px solid rgba(15, 23, 42, 0.05);
        background: rgba(255, 255, 255, 0.4);
      }
      .admin-content-area {
        flex: 1;
        padding: 32px;
        overflow-y: auto;
      }
      .analytics-card-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 24px;
        margin-bottom: 32px;
      }
      .analytics-card {
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        padding: 24px;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        position: relative;
        overflow: hidden;
      }
      [data-theme="light"] .analytics-card {
        background: rgba(255, 255, 255, 0.7);
        border: 1px solid rgba(15, 23, 42, 0.06);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
      }
      .analytics-card:hover {
        transform: translateY(-6px);
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.15);
        box-shadow: 0 20px 40px rgba(0,0,0,0.25);
      }
      [data-theme="light"] .analytics-card:hover {
        background: rgba(255, 255, 255, 0.95);
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
      }
      .analytics-card::before {
        content: '';
        position: absolute;
        width: 150px;
        height: 150px;
        background: radial-gradient(circle, var(--accent-color, #6B4EE6) 0%, transparent 70%);
        opacity: 0.15;
        top: -75px;
        right: -75px;
        pointer-events: none;
      }
      .analytics-card-val {
        font-size: 2.8rem;
        font-weight: 800;
        margin: 8px 0;
        color: var(--text-main);
      }
      .analytics-card-label {
        font-size: 0.9rem;
        color: var(--text-secondary);
        font-weight: 600;
      }
      .sidebar-toggle-btn {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.08);
        color: var(--text-main);
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;
      }
      [data-theme="light"] .sidebar-toggle-btn {
        background: rgba(15, 23, 42, 0.04);
        border: 1px solid rgba(15, 23, 42, 0.08);
      }
      .sidebar-toggle-btn:hover {
        background: rgba(255, 255, 255, 0.15);
      }
      [data-theme="light"] .sidebar-toggle-btn:hover {
        background: rgba(15, 23, 42, 0.08);
      }
      .stats-modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      }
      .stats-modal {
        background: #1e1e38;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 24px;
        width: 90%;
        max-width: 600px;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 24px 60px rgba(0,0,0,0.5);
      }
      [data-theme="light"] .stats-modal {
        background: white;
        border: 1px solid rgba(15, 23, 42, 0.1);
        box-shadow: 0 20px 50px rgba(15,23,42,0.15);
      }
      .stats-modal-header {
        padding: 24px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      [data-theme="light"] .stats-modal-header {
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
      }
      .stats-modal-content {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/users`);
      if (!res.ok) throw new Error('Failed to fetch users');
      const body = await res.json();
      setUsers(body.users || body);
    } catch (e) {
      console.warn('Failed to fetch users', e);
    }
  };

  const fetchFaculty = async () => {
    try {
      const res = await fetch(`${API_URL}/faculty`);
      if (!res.ok) throw new Error('Failed to fetch faculty');
      const body = await res.json();
      setFaculty(body.faculty || body);
    } catch (e) {
      console.warn('Failed to fetch faculty', e);
    }
  };

  const fetchFormationTasks = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
      const res = await fetch(`${API_URL}/formation/tasks?email=${user.email || ''}`);
      if (res.ok) {
        const data = await res.json();
        setFormationTasks(data.tasks || []);
      }
    } catch (e) {
      console.warn('Failed to fetch formation tasks', e);
    }
  };

  const fetchClosedTasks = async () => {
    try {
      const u = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
      const res = await fetch(`${API_URL}/formation/tasks/closed?email=${u.email || ''}`);
      if (res.ok) {
        const data = await res.json();
        setClosedTasks(data.tasks || []);
      }
    } catch (e) {
      console.warn('Failed to fetch closed tasks', e);
    }
  };

  const handleZipExport = async (taskId, taskName) => {
    setZipProgress({ taskId, logs: ['⏳ Initiating background export job...'] });
    try {
      const response = await fetch(`${API_URL}/formation/tasks/${taskId}/export-zip/start`, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to start export');
      
      const { jobId } = await response.json();
      const evtSource = new EventSource(`${API_URL}/formation/tasks/export-zip/progress/${jobId}`);
      
      evtSource.addEventListener('log', (e) => {
        const msg = JSON.parse(e.data);
        setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, msg] } : null);
        setTimeout(() => zipLogsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      });

      evtSource.addEventListener('DONE', (e) => {
        evtSource.close();
        setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, '🎉 ZIP is ready! Triggering download...'] } : null);
        
        const a = document.createElement('a');
        a.href = `${API_URL}/formation/tasks/export-zip/download/${jobId}`;
        a.download = `${taskName}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        setTimeout(() => setZipProgress(null), 3000); // auto-close after 3s
      });

      evtSource.addEventListener('ERROR', (e) => {
        evtSource.close();
        const err = JSON.parse(e.data);
        setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, `❌ Error: ${err.error}`] } : null);
      });

    } catch (e) {
      setZipProgress(prev => prev ? { ...prev, logs: [...prev.logs, `❌ Failed: ${e.message}`] } : null);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      const res = await fetch(`${API_URL}/feedback/results`);
      if (res.ok) {
        const data = await res.json();
        setFeedbacks(data.results || []);
      }
    } catch (e) {
      console.warn('Failed to fetch feedbacks', e);
    }
  };

  const fetchData = () => {
    fetchUsers();
    fetchFaculty();
    fetchFormationTasks();
    fetchClosedTasks();
    fetchFeedbacks();
  };

  // Attendance monitoring fetch
  const fetchAttSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/attendance/admin/summary`);
      const data = await res.json();
      setAttSummary(data);
    } catch (e) { console.warn('Failed to fetch attendance summary', e); }
  }, []);

  const fetchAttRequests = useCallback(async () => {
    setAttLoading(true);
    try {
      const params = new URLSearchParams();
      if (attFilter.status) params.append('status', attFilter.status);
      if (attFilter.date) params.append('date', attFilter.date);
      if (attFilter.department) params.append('department', attFilter.department);
      const res = await fetch(`${API_URL}/attendance/admin/requests?${params.toString()}`);
      const data = await res.json();
      setAttRequests(Array.isArray(data) ? data : []);
    } catch (e) { console.warn('Failed to fetch attendance requests', e); }
    finally { setAttLoading(false); }
  }, [attFilter]);

  // Email logs fetch
  const fetchEmailLogs = useCallback(async () => {
    setEmailLoading(true);
    try {
      const params = new URLSearchParams();
      if (emailFilter.type) params.append('type', emailFilter.type);
      if (emailFilter.status) params.append('status', emailFilter.status);
      if (emailFilter.date) params.append('date', emailFilter.date);
      const res = await fetch(`${API_URL}/email-logs?${params.toString()}`);
      const data = await res.json();
      setEmailLogs(data.logs || []);
    } catch (e) { console.warn('Failed to fetch email logs', e); }
    finally { setEmailLoading(false); }
  }, [emailFilter]);

  const fetchEmailStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/email-logs/stats`);
      const data = await res.json();
      setEmailStats(data);
    } catch (e) { console.warn('Failed to fetch email stats', e); }
  }, []);

  const handleRetrigger = async (logId) => {
    setRetriggerLoading(prev => ({ ...prev, [logId]: true }));
    try {
      const res = await fetch(`${API_URL}/email-logs/retrigger/${logId}`, { method: 'POST' });
      const data = await res.json();
      alert(data.message || 'Retrigger complete');
      fetchEmailLogs();
      fetchEmailStats();
    } catch (e) { alert('Retrigger failed'); }
    finally { setRetriggerLoading(prev => ({ ...prev, [logId]: false })); }
  };

  const handleRetriggerBatch = async (batchId) => {
    if (!window.confirm('Re-send ALL emails in this batch?')) return;
    setRetriggerLoading(prev => ({ ...prev, [`batch-${batchId}`]: true }));
    try {
      const res = await fetch(`${API_URL}/email-logs/retrigger-batch/${batchId}`, { method: 'POST' });
      const data = await res.json();
      alert(data.message || 'Batch retrigger complete');
      fetchEmailLogs();
      fetchEmailStats();
    } catch (e) { alert('Batch retrigger failed'); }
    finally { setRetriggerLoading(prev => ({ ...prev, [`batch-${batchId}`]: false })); }
  };

  // Broadcast fetch
  const fetchBroadcastLogs = useCallback(async () => {
    setBroadcastLoading(true);
    try {
      const res = await fetch(`${API_URL}/broadcast/logs`);
      const data = await res.json();
      setBroadcastLogs(data.logs || []);
    } catch (e) { console.warn('Failed to fetch broadcast logs', e); }
    finally { setBroadcastLoading(false); }
  }, []);

  const fetchBroadcastStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/broadcast/stats`);
      const data = await res.json();
      setBroadcastStats(data);
    } catch (e) { console.warn('Failed to fetch broadcast stats', e); }
  }, []);

  const sendBroadcast = async () => {
    if (!window.confirm(`Send broadcast email to ${broadcastAudience === 'all' ? 'ALL faculty + students' : broadcastAudience === 'faculty' ? 'ALL faculty' : 'ALL students'}?`)) return;
    setBroadcastSending(true);
    try {
      const htmlBody = `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #4F46E5, #7C3AED, #EC4899); padding: 32px; border-radius: 16px 16px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 1.8rem;">VJLINK</h1>
            <p style="color: rgba(255,255,255,0.9); font-size: 1.1rem; margin: 8px 0 0;">Moving to the Next Phase!</p>
          </div>
          <div style="background: #f9fafb; padding: 32px; border: 1px solid #e5e7eb; border-top: none;">
            <p style="font-size: 1.05rem;">Hello \ud83d\udc4b</p>
            <p>This is a test mail for <strong style="color: #4F46E5;">VJLINK</strong> as we move into the next stage of development \ud83d\ude80</p>
            <p>We're excited to share that VJLINK is progressing fast and getting ready for a <strong>complete launch of all modules</strong>.</p>
            <div style="background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin: 20px 0;">
              <p style="font-weight: bold; color: #4F46E5; margin-top: 0;">\ud83d\udc49 Get ready to experience:</p>
              <ul style="list-style: none; padding: 0; margin: 0;">
                <li style="padding: 8px 0; border-bottom: 1px solid #f3f4f6;">\ud83d\udc64 <strong>Student & Faculty Profiles</strong></li>
                <li style="padding: 8px 0; border-bottom: 1px solid #f3f4f6;">\u2705 <strong>Smart Team Formation</strong></li>
                <li style="padding: 8px 0; border-bottom: 1px solid #f3f4f6;">\ud83d\udce2 <strong>Announcements System</strong></li>
                <li style="padding: 8px 0; border-bottom: 1px solid #f3f4f6;">\ud83d\udccb <strong>Attendance Workflow</strong></li>
                <li style="padding: 8px 0;">\ud83d\udd25 <strong>And much more\u2026</strong></li>
              </ul>
            </div>
            <div style="text-align: center; margin: 28px 0;">
              <a href="https://vjlink.vjstartup.com" style="display: inline-block; background: linear-gradient(135deg, #4F46E5, #7C3AED); color: white; padding: 14px 36px; border-radius: 10px; text-decoration: none; font-weight: bold; font-size: 1.1rem; box-shadow: 0 4px 15px rgba(79,70,229,0.4);">\ud83c\udf10 Visit VJLINK \u2192</a>
              <p style="margin-top: 10px; color: #6b7280; font-size: 0.9rem;">vjlink.vjstartup.com</p>
            </div>
            <p>The full platform launch is <strong style="color: #EC4899;">coming soon</strong> \ud83d\udd25</p>
            <p>Stay tuned and be ready to explore VJLINK!</p>
            <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;">
            <p style="margin-bottom: 0;">Thank you \ud83d\ude4c</p>
            <p style="color: #4F46E5; font-weight: bold; margin-top: 4px;">Team VJLINK</p>
          </div>
          <div style="background: #1F2937; padding: 16px; border-radius: 0 0 16px 16px; text-align: center;">
            <p style="color: #9CA3AF; margin: 0; font-size: 0.8rem;">VNR Vignana Jyothi Institute of Engineering & Technology</p>
          </div>
        </div>
      `;
      const res = await fetch(`${API_URL}/broadcast/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: 'VJLINK \u2013 Moving to the Next Phase!', html: htmlBody, audience: broadcastAudience })
      });
      const data = await res.json();
      alert(data.message || 'Broadcast complete');
      fetchBroadcastLogs();
      fetchBroadcastStats();
    } catch (e) { alert('Broadcast failed: ' + e.message); }
    finally { setBroadcastSending(false); }
  };

  useEffect(() => {
    const userStr = localStorage.getItem('vjconnect_user');
    if (!userStr) { navigate('/'); return; }
    const userData = JSON.parse(userStr);
    const isAdminEmail = [
      String(ADMIN_EMAIL).toLowerCase(), 
      String(CO_ADMIN_EMAIL).toLowerCase(),
      String(TEST_ADMIN_EMAIL).toLowerCase()
    ].includes(userData.email?.toLowerCase());

    if (!isAdminEmail) {
      navigate('/');
      return;
    }
    setUser(userData);
    setIsAllowed(true); // Set isAllowed to true for rendering
    fetchFormationTasks();
    fetchClosedTasks();
    fetchUsers();
    fetchFaculty();
    fetchFeedbacks();
    // Polling for data updates
    pollingRef.current = setInterval(() => {
      fetchFormationTasks();
      fetchClosedTasks();
      fetchUsers();
      fetchFaculty();
    }, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [navigate]);

  // Fetch attendance and email data when those tabs are active
  useEffect(() => {
    if (currentTab === 'attendance') { fetchAttSummary(); fetchAttRequests(); }
  }, [currentTab, fetchAttSummary, fetchAttRequests]);

  useEffect(() => {
    if (currentTab === 'emails') { fetchEmailLogs(); fetchEmailStats(); }
  }, [currentTab, fetchEmailLogs, fetchEmailStats]);

  useEffect(() => {
    if (currentTab === 'broadcast') { fetchBroadcastLogs(); fetchBroadcastStats(); }
  }, [currentTab, fetchBroadcastLogs, fetchBroadcastStats]);

  useEffect(() => {
    if (currentTab === 'events') { 
      fetchAdminEvents(); 
    }
  }, [currentTab, fetchAdminEvents]);

  useEffect(() => {
    if (currentTab === 'events' && selectedEventId) { 
      fetchEventStats(selectedEventId); 
      fetchAdminParticipants(selectedEventId);
      const interval = setInterval(() => {
        fetchEventStats(selectedEventId);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [currentTab, selectedEventId, fetchEventStats, fetchAdminParticipants]);

  useEffect(() => {
    if (currentTab === 'test-faculty') {
      fetchTestLoginsStatus(true);
    }
  }, [currentTab, fetchTestLoginsStatus]);

  const toggleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') setSortDir('desc');
    else if (sortDir === 'desc') { setSortField(''); setSortDir('asc'); }
    else setSortDir('asc');
  };

  // Dynamic Input Helpers
  const [sectionRuleDept, setSectionRuleDept] = useState("CSE");
  const [sectionRuleCount, setSectionRuleCount] = useState(2);
  const [visRuleDept, setVisRuleDept] = useState("CSE");
  const [visRuleSections, setVisRuleSections] = useState([]); // NEW: Selected sections for current dept
  const [availableDepts, setAvailableDepts] = useState(['CSE', 'ECE', 'EEE', 'EIE', 'IT', 'MECH', 'CIVIL', 'AIML', 'AIDS', 'IOT']);
  const availableSections = ['A', 'B', 'C', 'D']; // Standard sections
  const availableYears = ['1st year', '2nd year', '3rd year', '4th year']; // Standard years


  useEffect(() => {
    // Populate available departments from users + faculty + static list
    const unique = new Set(['CSE', 'ECE', 'EEE', 'EIE', 'IT', 'MECH', 'CIVIL', 'AIML', 'AIDS', 'IOT']);
    if (users) users.forEach(u => { if (u.department) unique.add(u.department.toUpperCase()); });
    setAvailableDepts(Array.from(unique).sort());
  }, [users]);

  const addSectionRule = (isEdit = false) => {
    const setter = isEdit ? setEditingTask : setNewTask;
    setter(prev => ({
      ...prev,
      sectionLimits: { ...(prev.sectionLimits || {}), [sectionRuleDept]: parseInt(sectionRuleCount) }
    }));
  };

  const addVisRule = (isEdit = false) => {
    const setter = isEdit ? setEditingTask : setNewTask;
    setter(prev => {
      const currentDepts = prev.visibility_rules?.departments || [];
      const newSections = { ...(prev.visibility_rules?.sections || {}) };

      // Update sections if provided, otherwise default to all (remove from explicit sections list if it was there)
      if (visRuleSections.length > 0) {
        const existing = newSections[visRuleDept] || [];
        const unique = new Set([...existing, ...visRuleSections]);
        newSections[visRuleDept] = Array.from(unique).sort();
      } else {
        delete newSections[visRuleDept]; // Empty means all sections
      }

      // Add dept if not present
      const nextDepts = currentDepts.includes(visRuleDept) ? currentDepts : [...currentDepts, visRuleDept].sort();

      return {
        ...prev,
        visibility_rules: {
          ...(prev.visibility_rules || {}),
          departments: nextDepts,
          sections: newSections
        }
      };
    });

    // Reset section selection after adding
    setVisRuleSections([]);
  };

  const handleCreateTask = async () => {
    const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
    try {
      const res = await fetch(`${API_URL}/formation/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTask.name,
          creator_email: user.email,
          max_members: newTask.max_members,
          rules: JSON.stringify({ // Stringify rules
            sectionLimits: newTask.sectionLimits,
            genderRequirements: newTask.genderRequirements
          }),
          visibility_rules: JSON.stringify(newTask.visibility_rules || {}), // Stringify visibility
          expires_at: newTask.expires_at || null
        })
      });
      if (res.ok) {
        setShowTaskForm(false);
        fetchFormationTasks();
        setNewTask({ name: "", max_members: 6, sectionLimits: {}, genderRequirements: { female: 2 }, visibility_rules: {}, expires_at: "" });
      } else {
        alert("Failed to create task");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    try {
      const res = await fetch(`${API_URL}/formation/tasks/${editingTask.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingTask.name,
          max_members: editingTask.max_members,
          rules: editingTask.rules,
          visibility_rules: editingTask.visibility_rules,
          expires_at: editingTask.expires_at || null
        })
      });
      if (res.ok) {
        setShowEditModal(false);
        fetchFormationTasks();
        setEditingTask(null);
      } else {
        alert("Failed to update task");
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!isAllowed) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p>You do not have permission to view the admin dashboard.</p>
        <button onClick={() => navigate(user.role === 'faculty' ? '/faculty/home' : '/home')} style={{ marginTop: 12 }}>Back to Home</button>
      </div>
    );
  }


  const renderFormationTab = () => (
    <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: 'var(--text-main)', margin: 0 }}>Formation Tasks</h2>
        {formationSubTab === 'active' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn-profile btn-profile-secondary"
              onClick={() => setShowExcelModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FileSpreadsheet size={16} /> Create via Excel
            </button>
            <button className="btn-profile btn-profile-primary" onClick={() => setShowTaskForm(true)}>+ Create New Task</button>
          </div>
        )}
      </div>

      {/* Sub-Tabs: Active / Past */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--glass-border)' }}>
        {[{ key: 'active', label: '⚡ Active', count: formationTasks.length }, { key: 'past', label: '🗂️ Past', count: closedTasks.length }].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFormationSubTab(tab.key)}
            style={{
              padding: '10px 22px', border: 'none', cursor: 'pointer',
              background: formationSubTab === tab.key ? 'var(--accent-color)' : 'transparent',
              color: formationSubTab === tab.key ? '#fff' : 'var(--text-main)',
              borderTopLeftRadius: 8, borderTopRightRadius: 8, fontWeight: 'bold',
              borderBottom: formationSubTab === tab.key ? '2px solid var(--accent-color)' : '2px solid transparent',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 8
            }}
          >
            {tab.label}
            <span style={{
              background: formationSubTab === tab.key ? 'rgba(255,255,255,0.25)' : 'var(--glass-border)',
              color: formationSubTab === tab.key ? '#fff' : 'var(--text-muted)',
              borderRadius: 20, padding: '1px 8px', fontSize: 12, fontWeight: 'bold'
            }}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* ── ACTIVE TAB ── */}
      {formationSubTab === 'active' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {formationTasks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No active formation tasks</div>
          )}
          {formationTasks.map(t => (
            <div key={t.id} style={{ background: 'var(--glass-bg)', padding: 16, borderRadius: 8, border: '1px solid var(--glass-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h3 style={{ margin: 0, color: 'var(--text-main)' }}>{t.name}</h3>
                    <span style={{
                      background: t.status === 'locked' ? '#F59E0B20' : '#10B98120',
                      color: t.status === 'locked' ? '#F59E0B' : '#10B981',
                      padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 'bold'
                    }}>{t.status === 'locked' ? '🔒 Locked' : '✅ Active'}</span>
                  </div>
                  <p style={{ margin: '4px 0', fontSize: 14, color: t.expires_at && new Date(t.expires_at) < new Date() ? '#dc3545' : 'var(--text-secondary)' }}>
                    Max: {t.max_members} members | Created: {new Date(t.created_at).toLocaleDateString()}
                    {t.expires_at && (
                      <> | <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Expires: {new Date(t.expires_at).toLocaleString()}</span></>
                    )}
                  </p>
                  {t.visibility_rules && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>
                      Target: {(typeof t.visibility_rules === 'string' ? JSON.parse(t.visibility_rules) : t.visibility_rules)?.years?.join(', ') || 'All Years'}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <a
                    href={`${API_URL}/formation/tasks/${t.id}/export`}
                    download
                    className="btn-profile btn-profile-secondary"
                    style={{ textDecoration: 'none', padding: '8px 12px', fontSize: 14 }}
                  >📥 Export Excel</a>
                  <button
                    className="btn-profile btn-profile-secondary"
                    style={{ padding: '8px 12px', fontSize: 14, background: '#10B981', color: 'white', border: 'none' }}
                    onClick={() => handleZipExport(t.id, t.name)}
                    disabled={zipProgress && zipProgress.taskId === t.id}
                  >
                    {zipProgress && zipProgress.taskId === t.id ? '⏳ Exporting...' : '📦 Export ZIP'}
                  </button>
                  <button className="btn-profile btn-profile-secondary" onClick={() => navigate(`/admin/teams/${t.id}`)}>👥 View Teams</button>
                  <button
                    className="btn-profile btn-profile-secondary"
                    onClick={() => {
                      setEditingTask({
                        ...t,
                        rules: typeof t.rules === 'string' ? JSON.parse(t.rules) : t.rules,
                        visibility_rules: typeof t.visibility_rules === 'string' ? JSON.parse(t.visibility_rules) : t.visibility_rules,
                        expires_at: t.expires_at ? t.expires_at.slice(0, 16) : ""
                      });
                      setShowEditModal(true);
                    }}
                  >✏️ Edit</button>
                  <button
                    className="btn-profile btn-profile-secondary"
                    onClick={() => { setSelectedTaskForCollaboration(t); setShowCollaborationModal(true); }}
                  >🔑 Manage Access</button>
                  <button
                    style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#dc3545,#c82333)', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}
                    onClick={async () => {
                      if (!window.confirm(`Close "${t.name}"? It will be moved to Past Formations (not permanently deleted).`)) return;
                      try {
                        const res = await fetch(`${API_URL}/formation/tasks/${t.id}?email=${user.email}`, { method: 'DELETE' });
                        if (res.ok) { fetchFormationTasks(); fetchClosedTasks(); }
                        else alert('Failed to close task');
                      } catch (err) { console.error(err); }
                    }}
                  >🗂 Close Task</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── PAST TAB ── */}
      {formationSubTab === 'past' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {closedTasks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🗂️</div>
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>No past formations yet</div>
              <div style={{ fontSize: 13 }}>Closed formation tasks will appear here</div>
            </div>
          )}
          {closedTasks.map(t => (
            <div key={t.id} style={{
              background: 'var(--glass-bg)', padding: 16, borderRadius: 8,
              border: '1px solid var(--glass-border)',
              opacity: 0.92
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, color: 'var(--text-main)' }}>{t.name}</h3>
                    <span style={{ background: '#6b728020', color: '#9CA3AF', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>🗂 Closed</span>
                    {t.team_count > 0 && (
                      <span style={{ background: '#6366F120', color: '#6366F1', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>{t.team_count} teams</span>
                    )}
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                    Created by: {t.display_creator_name || t.creator_email} &nbsp;|&nbsp;
                    Created: {new Date(t.created_at).toLocaleDateString()}
                    {t.closed_at && <> &nbsp;|&nbsp; <span style={{ color: '#EF4444' }}>Closed: {new Date(t.closed_at).toLocaleDateString()}</span></>}
                  </p>
                  {t.visibility_rules && (
                    <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#888' }}>
                      Target: {(typeof t.visibility_rules === 'string' ? JSON.parse(t.visibility_rules) : t.visibility_rules)?.years?.join(', ') || 'All Years'}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <a
                    href={`${API_URL}/formation/tasks/${t.id}/export`}
                    download
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: '1px solid var(--glass-border)',
                      background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer',
                      fontWeight: 'bold', fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5
                    }}
                  >📥 Export Excel</a>
                  <button
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: 'none',
                      background: '#10B981', color: 'white', cursor: 'pointer',
                      fontWeight: 'bold', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: (zipProgress && zipProgress.taskId === t.id) ? 0.7 : 1
                    }}
                    onClick={() => handleZipExport(t.id, t.name)}
                    disabled={zipProgress && zipProgress.taskId === t.id}
                  >
                    {zipProgress && zipProgress.taskId === t.id ? '⏳ Exporting...' : '📦 Export ZIP'}
                  </button>
                  <button
                    style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}
                    onClick={() => navigate(`/admin/teams/${t.id}`)}
                  >👥 View Teams</button>
                  <button
                    style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none',
                      background: 'linear-gradient(135deg, #10B981, #059669)',
                      color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: 13
                    }}
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/formation/tasks/${t.id}/restore`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: user.email })
                        });
                        if (res.ok) { fetchFormationTasks(); fetchClosedTasks(); }
                        else { const d = await res.json(); alert(d.error || 'Failed to restore'); }
                      } catch (err) { console.error(err); }
                    }}
                  >⟳ Restore</button>
                  <button
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: 'none',
                      background: '#7f1d1d', color: '#fca5a5', cursor: 'pointer',
                      fontWeight: 'bold', fontSize: 13
                    }}
                    onClick={async () => {
                      if (!window.confirm(`Permanently delete "${t.name}" and ALL its teams? This CANNOT be undone.`)) return;
                      try {
                        const res = await fetch(`${API_URL}/formation/tasks/${t.id}/permanent?email=${user.email}`, { method: 'DELETE' });
                        if (res.ok) { fetchClosedTasks(); }
                        else { const d = await res.json(); alert(d.error || 'Failed to delete'); }
                      } catch (err) { console.error(err); }
                    }}
                  >🗑 Delete Permanently</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Task Modal */}
      {showTaskForm && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="glass-card" style={{ background: '#fff', padding: 30, maxWidth: 500, width: '90%', color: '#333', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20 }}>Create Formation Task</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Task Name</label>
                <input className="profile-input" value={newTask.name} onChange={e => setNewTask({ ...newTask, name: e.target.value })} />
              </div>
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Max Members</label>
                <input type="number" className="profile-input" value={newTask.max_members} onChange={e => setNewTask({ ...newTask, max_members: e.target.value })} />
              </div>
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Expiration Time</label>
                <input type="datetime-local" className="profile-input" value={newTask.expires_at || ""} onChange={e => setNewTask({ ...newTask, expires_at: e.target.value })} />
              </div>

              {/* Section Limits */}
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Section Limits (Max students per dept)</label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <select className="profile-input" style={{ flex: 1 }} value={sectionRuleDept} onChange={e => setSectionRuleDept(e.target.value)}>
                    {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <input type="number" className="profile-input" style={{ width: 80 }} value={sectionRuleCount} onChange={e => setSectionRuleCount(e.target.value)} />
                  <button className="btn-profile btn-profile-secondary" onClick={() => addSectionRule(false)}>Add</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {Object.entries(newTask.sectionLimits || {}).map(([d, c]) => (
                    <div key={d} style={{ background: '#f0f9ff', padding: '4px 8px', borderRadius: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span>{d}: {c}</span>
                      <span style={{ cursor: 'pointer', color: 'red' }} onClick={() => setNewTask(prev => {
                        const next = { ...prev.sectionLimits }; delete next[d]; return { ...prev, sectionLimits: next };
                      })}>&times;</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visibility: Departments & Sections */}
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Access Control</label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <select className="profile-input" style={{ flex: 1 }} value={visRuleDept} onChange={e => setVisRuleDept(e.target.value)}>
                    {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <button className="btn-profile btn-profile-secondary" onClick={() => addVisRule(false)}>Add Dept</button>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: '0.9rem', marginBottom: 5, display: 'block' }}>Sections (Optional):</label>
                  <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
                    {availableSections.map(s => (
                      <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="checkbox" checked={visRuleSections.includes(s)} onChange={e => e.target.checked ? setVisRuleSections([...visRuleSections, s]) : setVisRuleSections(visRuleSections.filter(x => x !== s))} />
                        <span>{s}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Years Selection */}
                <div style={{ marginTop: 15, padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <label style={{ fontSize: '0.9rem', marginBottom: 8, display: 'block', fontWeight: 'bold' }}>Target Years:</label>
                  <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
                    {availableYears.map(year => (
                      <label key={year} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="checkbox" checked={newTask.visibility_rules?.years?.includes(year)} onChange={e => {
                          setNewTask(prev => {
                            const cur = prev.visibility_rules?.years || [];
                            const next = e.target.checked ? [...cur, year] : cur.filter(y => y !== year);
                            return { ...prev, visibility_rules: { ...(prev.visibility_rules || {}), years: next } };
                          });
                        }} />
                        <span>{year}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Selected Rules List */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                  {newTask.visibility_rules?.departments?.map(dept => {
                    const sections = newTask.visibility_rules?.sections?.[dept];
                    return (
                      <div key={dept} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{dept} {sections ? `(${sections.join(', ')})` : ''}</span>
                        <span style={{ cursor: 'pointer', color: 'red' }} onClick={() => setNewTask(prev => {
                          const depts = prev.visibility_rules.departments.filter(d => d !== dept);
                          const newSecs = { ...prev.visibility_rules.sections }; delete newSecs[dept];
                          return { ...prev, visibility_rules: { ...prev.visibility_rules, departments: depts, sections: newSecs } };
                        })}>&times;</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Min Female Members</label>
                <input type="number" className="profile-input" value={newTask.genderRequirements?.female || 0} onChange={e => setNewTask({ ...newTask, genderRequirements: { ...newTask.genderRequirements, female: parseInt(e.target.value) || 0 } })} />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button className="btn-profile btn-profile-secondary" style={{ flex: 1 }} onClick={() => setShowTaskForm(false)}>Cancel</button>
                <button className="btn-profile btn-profile-primary" style={{ flex: 1 }} onClick={handleCreateTask}>Create Task</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && editingTask && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="glass-card" style={{ background: '#fff', padding: 30, maxWidth: 500, width: '90%', color: '#333', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: 20 }}>Edit Formation Task</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Task Name</label>
                <input className="profile-input" value={editingTask.name} onChange={e => setEditingTask({ ...editingTask, name: e.target.value })} />
              </div>

              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Expiration Time (Locking Time)</label>
                <input type="datetime-local" className="profile-input" value={editingTask.expires_at || ""} onChange={e => setEditingTask({ ...editingTask, expires_at: e.target.value })} />
              </div>

              {/* Section Limits Edit */}
              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Section Limits (Max students per dept)</label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <select className="profile-input" style={{ flex: 1 }} value={sectionRuleDept} onChange={e => setSectionRuleDept(e.target.value)}>
                    {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <input type="number" className="profile-input" style={{ width: 80 }} value={sectionRuleCount} onChange={e => setSectionRuleCount(e.target.value)} />
                  <button className="btn-profile btn-profile-secondary" onClick={() => addSectionRule(true)}>Add</button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {Object.entries((typeof editingTask.rules === 'string' ? JSON.parse(editingTask.rules) : editingTask.rules)?.sectionLimits || {}).map(([d, c]) => (
                    <div key={d} style={{ background: '#f0f9ff', padding: '4px 8px', borderRadius: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span>{d}: {c}</span>
                      <span style={{ cursor: 'pointer', color: 'red' }} onClick={() => setEditingTask(prev => {
                        const rules = typeof prev.rules === 'string' ? JSON.parse(prev.rules) : (prev.rules || {});
                        const limits = { ...(rules.sectionLimits || {}) };
                        delete limits[d];
                        return { ...prev, rules: { ...rules, sectionLimits: limits } };
                      })}>&times;</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Visibility Settings</label>
                <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap', marginBottom: 15 }}>
                  {availableYears.map(year => (
                    <label key={year} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <input
                        type="checkbox"
                        checked={editingTask.visibility_rules?.years?.includes(year)}
                        onChange={e => {
                          setEditingTask(prev => {
                            const cur = prev.visibility_rules?.years || [];
                            const next = e.target.checked ? [...cur, year] : cur.filter(y => y !== year);
                            return { ...prev, visibility_rules: { ...(prev.visibility_rules || {}), years: next } };
                          });
                        }}
                      />
                      <span>{year}</span>
                    </label>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <select className="profile-input" style={{ flex: 1 }} value={visRuleDept} onChange={e => setVisRuleDept(e.target.value)}>
                    {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <button className="btn-profile btn-profile-secondary" onClick={() => addVisRule(true)}>Add Dept Access</button>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: '0.9rem', marginBottom: 5, display: 'block' }}>Sections (Optional):</label>
                  <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
                    {availableSections.map(s => (
                      <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input type="checkbox" checked={visRuleSections.includes(s)} onChange={e => e.target.checked ? setVisRuleSections([...visRuleSections, s]) : setVisRuleSections(visRuleSections.filter(x => x !== s))} />
                        <span>{s}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Selected Rules List for Edit */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                  {editingTask.visibility_rules?.departments?.map(dept => {
                    const sections = editingTask.visibility_rules?.sections?.[dept];
                    return (
                      <div key={dept} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>{dept} {sections ? `(${sections.join(', ')})` : ''}</span>
                        <span style={{ cursor: 'pointer', color: 'red' }} onClick={() => setEditingTask(prev => {
                          const depts = prev.visibility_rules.departments.filter(d => d !== dept);
                          const newSecs = { ...prev.visibility_rules.sections }; delete newSecs[dept];
                          return { ...prev, visibility_rules: { ...prev.visibility_rules, departments: depts, sections: newSecs } };
                        })}>&times;</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="profile-form-row">
                <label style={{ display: 'block', marginBottom: 5 }}>Min Female Members</label>
                <input
                  type="number"
                  className="profile-input"
                  value={(typeof editingTask.rules === 'string' ? JSON.parse(editingTask.rules) : editingTask.rules)?.genderRequirements?.female || 0}
                  onChange={e => setEditingTask(prev => {
                    const rules = typeof prev.rules === 'string' ? JSON.parse(prev.rules) : (prev.rules || {});
                    return { ...prev, rules: { ...rules, genderRequirements: { ...(rules.genderRequirements || {}), female: parseInt(e.target.value) || 0 } } };
                  })}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button className="btn-profile btn-profile-secondary" style={{ flex: 1 }} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn-profile btn-profile-primary" style={{ flex: 1 }} onClick={handleUpdateTask}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
    </div>
  );

  const renderUserTable = () => {
    const data = currentTab === 'students' ? users : faculty;
    const q = (filter || '').toLowerCase();
    const filtered = (data || []).filter(u => {
      if (!q) return true;
      return (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q) || (u.rollNo || '').toLowerCase().includes(q) || (u.designation || '').toLowerCase().includes(q);
    });
    const sorted = (sortField ? filtered.slice().sort((a, b) => {
      const va = String((a[sortField] || '')).toLowerCase();
      const vb = String((b[sortField] || '')).toLowerCase();
      if (va === vb) return 0;
      const cmp = va < vb ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    }) : filtered);

    return (
      <>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <input placeholder="Filter name or email" value={filter} onChange={e => setFilter(e.target.value)} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--glass-border)', background: 'var(--input-bg)', color: 'var(--input-text)' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => toggleSort('name')} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--glass-border)', background: sortField === 'name' ? 'var(--accent-color)' : 'var(--glass-bg)', color: sortField === 'name' ? 'white' : 'var(--text-main)' }}>
              Name {sortField === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </button>
            <button onClick={() => toggleSort('email')} style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--glass-border)', background: sortField === 'email' ? 'var(--accent-color)' : 'var(--glass-bg)', color: sortField === 'email' ? 'white' : 'var(--text-main)' }}>
              Email {sortField === 'email' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </button>
          </div>
          <button onClick={fetchData} style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-main)', cursor: 'pointer' }}>Refresh</button>
        </div>

        <div style={{ overflowX: 'auto', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 8, padding: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>#</th>
                <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Name</th>
                <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Email</th>
                {currentTab === 'students' ? (
                  <>
                    <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>RollNo</th>
                    <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Dept</th>
                    <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Year</th>
                  </>
                ) : (
                  <>
                    <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Designation</th>
                    <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Dept</th>
                  </>
                )}
                <th style={{ padding: '8px 12px', color: 'var(--text-main)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u, idx) => (
                <tr key={u.id || idx} style={{ borderBottom: '1px solid var(--glass-border)', color: 'var(--text-main)' }}>
                  <td style={{ padding: '10px 12px' }}>{u.id || '-'}</td>
                  <td style={{ padding: '10px 12px', display: 'flex', alignItems: 'center' }}>
                    {u.name || '-'}
                    {isGoldVerified(u) && <GoldVerifiedBadge size={16} style={{ marginLeft: 4 }} />}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{u.email || '-'}</td>
                  {currentTab === 'students' ? (
                    <>
                      <td style={{ padding: '10px 12px' }}>{u.rollNo || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{u.department || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{u.year || '-'}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '10px 12px' }}>{u.designation || '-'}</td>
                      <td style={{ padding: '10px 12px' }}>{u.work_dept || '-'}</td>
                    </>
                  )}
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => navigate(`/profile?email=${encodeURIComponent(u.email)}`)} style={{ padding: '6px 10px', borderRadius: 6 }}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderTestFacultyTab = () => {
    const filteredList = testLoginsList.filter(item => {
      if (!testLoginsSearch) return true;
      const q = testLoginsSearch.toLowerCase();
      return (
        (item.roll_no && item.roll_no.toLowerCase().includes(q)) ||
        (item.name && item.name.toLowerCase().includes(q)) ||
        (item.email && item.email.toLowerCase().includes(q)) ||
        (item.department && item.department.toLowerCase().includes(q))
      );
    });

    const activeCount = testLoginsList.filter(i => i.is_active).length;
    const pausedCount = testLoginsList.length - activeCount;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
        {/* Flash Message */}
        {testLoginsMessage && (
          <div style={{
            padding: '1rem 1.25rem',
            borderRadius: 14,
            background: testLoginsMessage.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : testLoginsMessage.type === 'warning' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(99, 102, 241, 0.15)',
            border: `1px solid ${testLoginsMessage.type === 'success' ? '#10B981' : testLoginsMessage.type === 'warning' ? '#F59E0B' : '#6366F1'}`,
            color: 'var(--text-main)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 14px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {testLoginsMessage.type === 'success' ? <CheckCircle size={20} color="#10B981" /> : <AlertCircle size={20} color="#F59E0B" />}
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{testLoginsMessage.text}</span>
            </div>
            <button
              onClick={() => setTestLoginsMessage(null)}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}
            >✕</button>
          </div>
        )}

        {/* Master Control Card */}
        <div style={{
          background: testLoginsEnabled
            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(99, 102, 241, 0.08) 100%)'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(100, 116, 139, 0.08) 100%)',
          border: `1.5px solid ${testLoginsEnabled ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
          borderRadius: 20,
          padding: '1.75rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1.5rem',
          boxShadow: '0 8px 30px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', maxWidth: 650 }}>
            <div style={{
              width: 58,
              height: 58,
              borderRadius: 16,
              background: testLoginsEnabled ? '#10B98122' : '#EF444422',
              color: testLoginsEnabled ? '#10B981' : '#EF4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <FlaskConical size={30} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-main)' }}>
                  Faculty Testing Mode
                </h2>
                <span style={{
                  background: testLoginsEnabled ? '#10B981' : '#EF4444',
                  color: '#ffffff',
                  padding: '3px 12px',
                  borderRadius: 20,
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase'
                }}>
                  {testLoginsEnabled ? '🟢 ENABLED' : '🔴 DISABLED'}
                </span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {testLoginsEnabled
                  ? 'Active test students sign in directly as Faculty to test faculty-specific portals and features. Normal students remain unaffected.'
                  : 'All student logins are strictly enforced as Students. Faculty test simulation is completely locked.'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={handleToggleGlobalTestLogins}
              style={{
                background: testLoginsEnabled ? '#ef4444' : '#10b981',
                color: '#ffffff',
                border: 'none',
                borderRadius: 14,
                padding: '0.85rem 1.6rem',
                fontSize: '0.95rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                boxShadow: testLoginsEnabled ? '0 4px 18px rgba(239, 68, 68, 0.4)' : '0 4px 18px rgba(16, 185, 129, 0.4)',
                transition: 'all 0.2s ease'
              }}
            >
              <Power size={18} />
              {testLoginsEnabled ? 'Disable Test Faculty Mode' : 'Enable Test Faculty Mode'}
            </button>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
          {[
            { label: 'Total Test Students', value: testLoginsList.length, color: '#6366F1', icon: <Users size={20} /> },
            { label: 'Active Faculty Testers', value: activeCount, color: '#10B981', icon: <CheckCircle size={20} /> },
            { label: 'Paused Testers', value: pausedCount, color: '#F59E0B', icon: <Clock size={20} /> },
            { label: 'Live Login Role', value: testLoginsEnabled ? 'FACULTY' : 'STUDENT', color: testLoginsEnabled ? '#10B981' : '#EF4444', icon: <Activity size={20} /> },
          ].map(stat => (
            <div key={stat.label} style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 16,
              padding: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1.25rem',
              boxShadow: '0 4px 14px rgba(0,0,0,0.03)'
            }}>
              <div style={{
                width: 46,
                height: 46,
                borderRadius: 12,
                background: `${stat.color}15`,
                color: stat.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {stat.icon}
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: stat.color, lineHeight: 1.2 }}>{stat.value}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Student Section */}
        <div style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 20,
          padding: '1.5rem 1.75rem',
          position: 'relative'
        }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.15rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={18} color="var(--accent-color)" /> Add Student to Faculty Testing
          </h3>
          <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Enter an existing student roll number (e.g. <code>24071A6673</code>) or student name. Select from the directory or add directly.
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', position: 'relative' }}>
            <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
              <input
                type="text"
                value={studentRollInput}
                onChange={e => handleStudentRollSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddTestLogin(); }}
                placeholder="Enter Student Roll No or Name (e.g. 24071A6680)..."
                style={{
                  width: '100%',
                  padding: '0.85rem 1.1rem',
                  borderRadius: 12,
                  border: '1px solid var(--glass-border)',
                  background: 'rgba(0,0,0,0.2)',
                  color: 'var(--text-main)',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box'
                }}
              />

              {/* Autocomplete Suggestions Dropdown */}
              {studentSearchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#161927',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 12,
                  marginTop: 6,
                  maxHeight: 280,
                  overflowY: 'auto',
                  zIndex: 50,
                  boxShadow: '0 12px 30px rgba(0,0,0,0.5)'
                }}>
                  {studentSearchResults.map(s => (
                    <div
                      key={s.id || s.email}
                      onClick={() => handleAddTestLogin(s)}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8, background: '#6366F125', color: '#6366F1',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.8rem'
                        }}>
                          {s.rollNo ? s.rollNo.slice(-2) : 'ST'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)' }}>
                            {s.name || s.rollNo}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {s.rollNo} • {s.department || 'B.Tech'} {s.section ? `(${s.section})` : ''} • {s.email}
                          </div>
                        </div>
                      </div>
                      <button
                        style={{
                          background: 'var(--accent-color)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '5px 12px',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => handleAddTestLogin()}
              disabled={addTestLoginLoading || !studentRollInput.trim()}
              style={{
                background: 'var(--accent-color)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 12,
                padding: '0.85rem 1.6rem',
                fontSize: '0.92rem',
                fontWeight: 700,
                cursor: (!studentRollInput.trim() || addTestLoginLoading) ? 'not-allowed' : 'pointer',
                opacity: (!studentRollInput.trim() || addTestLoginLoading) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                whiteSpace: 'nowrap'
              }}
            >
              <UserPlus size={16} />
              {addTestLoginLoading ? 'Adding...' : 'Add Test Login'}
            </button>
          </div>
        </div>

        {/* Active Test Logins Directory */}
        <div style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 20,
          padding: '1.5rem 1.75rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FlaskConical size={18} color="#10B981" /> Active Test Faculty Directory ({testLoginsList.length})
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                These students will be logged in as faculty when Master Test Mode is active.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ position: 'relative', width: 220 }}>
                <input
                  type="text"
                  placeholder="Filter test list..."
                  value={testLoginsSearch}
                  onChange={e => setTestLoginsSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.55rem 0.9rem',
                    borderRadius: 8,
                    border: '1px solid var(--glass-border)',
                    background: 'rgba(0,0,0,0.15)',
                    color: 'var(--text-main)',
                    fontSize: '0.85rem'
                  }}
                />
              </div>

              <button
                onClick={fetchTestLoginsStatus}
                title="Refresh List"
                style={{
                  padding: '0.55rem 0.9rem',
                  borderRadius: 8,
                  border: '1px solid var(--glass-border)',
                  background: 'var(--glass-bg)',
                  color: 'var(--text-main)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.85rem'
                }}
              >
                <RefreshCw size={14} className={testLoginsLoading ? 'spin' : ''} /> Refresh
              </button>
            </div>
          </div>

          {/* Table Container */}
          <div style={{ overflowX: 'auto' }}>
            {testLoginsLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                <RefreshCw size={24} className="spin" style={{ marginBottom: 10 }} />
                <div>Loading test accounts...</div>
              </div>
            ) : filteredList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                <FlaskConical size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
                <div style={{ fontWeight: 600 }}>No test faculty accounts found.</div>
                <div style={{ fontSize: '0.82rem', marginTop: 4 }}>Add a student roll number above to get started.</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--glass-border)', textAlign: 'left' }}>
                    {['#', 'Roll No', 'Student Name', 'College Email', 'Department', 'Current Login As', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 10px', color: 'var(--text-muted)', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((item, index) => {
                    const isFacultyNow = testLoginsEnabled && item.is_active;
                    return (
                      <tr key={item.id || index} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' }}>
                        <td style={{ padding: '12px 10px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                          {index + 1}
                        </td>
                        <td style={{ padding: '12px 10px', fontWeight: 800, color: 'var(--text-main)', letterSpacing: '0.02em' }}>
                          <span style={{
                            background: 'rgba(99, 102, 241, 0.12)',
                            color: '#818cf8',
                            padding: '3px 8px',
                            borderRadius: 6,
                            fontFamily: 'monospace',
                            fontSize: '0.85rem'
                          }}>
                            {item.roll_no}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px', fontWeight: 600, color: 'var(--text-main)' }}>
                          {item.name || '—'}
                        </td>
                        <td style={{ padding: '12px 10px', color: 'var(--text-muted)', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                          {item.email}
                        </td>
                        <td style={{ padding: '12px 10px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          {item.department || 'B.Tech'}
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <span style={{
                            background: isFacultyNow ? 'rgba(16, 185, 129, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                            color: isFacultyNow ? '#10B981' : '#94A3B8',
                            padding: '3px 10px',
                            borderRadius: 20,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5
                          }}>
                            {isFacultyNow ? '👨‍🏫 FACULTY' : '🎓 STUDENT'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <button
                            onClick={() => handleToggleUserTestLogin(item.id, item.is_active)}
                            style={{
                              background: item.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                              color: item.is_active ? '#10B981' : '#EF4444',
                              border: `1px solid ${item.is_active ? '#10B98155' : '#EF444455'}`,
                              borderRadius: 16,
                              padding: '4px 12px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            {item.is_active ? 'Active' : 'Paused'}
                          </button>
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <button
                            onClick={() => handleRemoveTestLogin(item.id, item.roll_no)}
                            title="Remove from Test Faculty"
                            style={{
                              background: 'rgba(239, 68, 68, 0.12)',
                              color: '#EF4444',
                              border: 'none',
                              borderRadius: 8,
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <Trash2 size={13} /> Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackTab = () => (
    <div style={{ marginTop: '20px' }}>
      <FeedbackDashboard feedbacks={feedbacks} onRefresh={fetchFeedbacks} />
    </div>
  );

  const renderAnalyticsTab = () => {
    return (
      <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 24, padding: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ color: 'var(--text-main)', margin: 0, fontSize: '1.8rem' }}>Login telemetry & active users</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0', fontSize: '0.95rem' }}>Track platform engagement and user logins</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Filter date:</span>
            <input
              type="date"
              value={statsDate}
              onChange={(e) => setStatsDate(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '10px',
                border: '1px solid var(--glass-border)',
                background: 'var(--input-bg)',
                color: 'var(--input-text)',
                outline: 'none',
                fontWeight: 600
              }}
            />
          </div>
        </div>

        {loadingStats ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
            <RefreshCw className="animate-spin" size={24} style={{ margin: '0 auto 12px' }} />
            <span>Loading telemetry...</span>
          </div>
        ) : (
          <>
            <div className="analytics-card-grid">
              <div className="analytics-card" onClick={() => {
                setStatsDetailTitle(`Active Users on ${new Date(statsDate).toLocaleDateString()}`);
                setShowUsersModal(true);
              }}>
                <div className="analytics-card-label">Active Users (Selected Day)</div>
                <div className="analytics-card-val">{loginStats.users?.length || 0}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>Click to view user list</div>
              </div>
              <div className="analytics-card" onClick={() => {
                setStatsDetailTitle("Unique Logins (Last 24 Hours)");
                setShowUsersModal(true);
              }}>
                <div className="analytics-card-label">Daily Unique Logins</div>
                <div className="analytics-card-val">{loginStats.stats?.day || 0}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>24h unique sessions</div>
              </div>
              <div className="analytics-card" onClick={() => {
                setStatsDetailTitle("Unique Logins (Last 7 Days)");
                setShowUsersModal(true);
              }}>
                <div className="analytics-card-label">Weekly Unique Logins</div>
                <div className="analytics-card-val">{loginStats.stats?.week || 0}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>7d unique sessions</div>
              </div>
              <div className="analytics-card" onClick={() => {
                setStatsDetailTitle("Unique Logins (Last 30 Days)");
                setShowUsersModal(true);
              }}>
                <div className="analytics-card-label">Monthly Unique Logins</div>
                <div className="analytics-card-val">{loginStats.stats?.month || 0}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>30d unique sessions</div>
              </div>
            </div>

            <div style={{ marginTop: 32 }}>
              <h3 style={{ color: 'var(--text-main)', marginBottom: 16 }}>Users Active on {new Date(statsDate).toLocaleDateString()} ({loginStats.users?.length || 0})</h3>
              {loginStats.users?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', border: '1px dashed var(--glass-border)', borderRadius: 16 }}>
                  No active login events recorded on this date.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid var(--glass-border)', borderRadius: 16, background: 'var(--glass-bg)', padding: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '12px 16px', color: 'var(--text-main)' }}>Name</th>
                        <th style={{ padding: '12px 16px', color: 'var(--text-main)' }}>Roll Number / Role</th>
                        <th style={{ padding: '12px 16px', color: 'var(--text-main)' }}>Email</th>
                        <th style={{ padding: '12px 16px', color: 'var(--text-main)' }}>Latest Login Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginStats.users.map((u, i) => (
                        <tr key={i} style={{ borderBottom: i === loginStats.users.length - 1 ? 'none' : '1px solid var(--glass-border)', color: 'var(--text-main)' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 'bold' }}>{u.name}</td>
                          <td style={{ padding: '12px 16px' }}>{u.rollNo}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{u.email}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{new Date(u.login_time).toLocaleTimeString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderUsersListModal = () => {
    if (!showUsersModal) return null;
    return (
      <div className="stats-modal-overlay" onClick={() => setShowUsersModal(false)}>
        <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
          <div className="stats-modal-header">
            <h3 style={{ color: 'var(--text-main)', margin: 0, fontSize: '1.25rem' }}>{statsDetailTitle}</h3>
            <button
              onClick={() => setShowUsersModal(false)}
              style={{
                background: 'none', border: 'none', color: 'var(--text-main)',
                fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center'
              }}
            >
              ×
            </button>
          </div>
          <div className="stats-modal-content">
            {loginStats.users?.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No users logged in on this day.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {loginStats.users.map((u, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 12
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>{u.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{u.email}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span
                        style={{
                          fontSize: '0.75rem', padding: '4px 8px', borderRadius: 20,
                          background: 'var(--accent-color)', color: 'white', fontWeight: 600
                        }}
                      >
                        {u.rollNo}
                      </span>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        {new Date(u.login_time).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEventsTab = () => {
    const curEvent = adminEvents.find(e => String(e.id) === String(selectedEventId)) || (adminEvents.length > 0 ? adminEvents[0] : null);

    const filteredParticipants = adminParticipants.filter(p => {
      if (!adminParticipantsSearch) return true;
      const q = adminParticipantsSearch.toLowerCase();
      return (p.name && p.name.toLowerCase().includes(q)) ||
             (p.rollNo && p.rollNo.toLowerCase().includes(q)) ||
             (p.department && p.department.toLowerCase().includes(q)) ||
             (p.email && p.email.toLowerCase().includes(q)) ||
             (p.school_name && p.school_name.toLowerCase().includes(q));
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Event Selector & Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', background: 'var(--glass-bg)', padding: '1.25rem', borderRadius: 16, border: '1px solid var(--glass-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-main)' }}>Select Event:</label>
            <select 
              value={selectedEventId} 
              onChange={e => {
                const newId = e.target.value;
                setSelectedEventId(newId);
                fetchEventStats(newId);
                fetchAdminParticipants(newId);
              }}
              style={{ padding: '0.6rem 1rem', borderRadius: 10, background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-main)', fontSize: '0.95rem', fontWeight: 600 }}
            >
              {adminEvents.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.name} ({ev.slug})</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button className="btn-profile btn-profile-primary" onClick={() => setShowCreateEvent(true)}>+ Create New Event</button>
            <button 
              onClick={() => {
                navigate(`/events/${curEvent?.slug || 'convergence2k26'}/admin`);
              }} 
              className="btn-profile btn-profile-secondary"
              style={{ background: 'rgba(139,92,246,0.15)', borderColor: 'rgba(139,92,246,0.3)', color: 'var(--text-main)' }}
              title="Open full dedicated Event Admin Portal"
            >
              ⚙️ Full Event Admin
            </button>
            <button 
              onClick={() => {
                navigate(`/events/${curEvent?.slug || 'convergence2k26'}`);
              }} 
              className="btn-profile btn-profile-secondary"
            >
              🌐 Student Hub
            </button>
            <button 
              onClick={() => {
                fetchAdminConnections(selectedEventId);
                setShowAdminConnectionsModal(true);
              }} 
              className="btn-profile btn-profile-secondary"
            >
              🤝 View Connections ({eventStats?.connection_count || 0})
            </button>
            {selectedEventId && (
              <button 
                className="btn-profile btn-profile-secondary" 
                style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                onClick={async () => {
                  if (!window.confirm(`Are you sure you want to permanently delete event "${curEvent?.name || selectedEventId}"? All sub-events, participant profiles, and connections will be permanently deleted.`)) return;
                  try {
                    const res = await fetch(`${API_URL}/events-admin/${selectedEventId}?admin_email=${user.email}`, { method: 'DELETE' });
                    const d = await res.json();
                    if (d.success) {
                      alert('Event deleted successfully!');
                      setSelectedEventId('');
                      fetchAdminEvents();
                    } else alert(d.error || 'Failed to delete event');
                  } catch(e) { alert('Error deleting event'); }
                }}
              >
                🗑 Delete Event
              </button>
            )}
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="analytics-card-grid">
          <div 
            className="analytics-card" 
            style={{ cursor: 'pointer' }}
            onClick={() => {
              const el = document.getElementById('events-participants-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            title="Click to view participants list"
          >
            <div className="analytics-card-val" style={{ color: 'var(--accent-color, #8b5cf6)' }}>{eventStats?.total_participants ?? adminParticipants.length}</div>
            <div className="analytics-card-label">Total Participants (View List ↓)</div>
          </div>
          <div 
            className="analytics-card" 
            style={{ cursor: 'pointer' }}
            onClick={() => {
              fetchAdminConnections(selectedEventId);
              setShowAdminConnectionsModal(true);
            }}
            title="Click to view connections"
          >
            <div className="analytics-card-val" style={{ color: '#3b82f6' }}>{eventStats?.connection_count || 0}</div>
            <div className="analytics-card-label">Connections Made (View 🤝)</div>
          </div>
          <div className="analytics-card">
            <div className="analytics-card-val" style={{ color: '#22c55e' }}>{eventStats?.profile_completion_avg || 0}%</div>
            <div className="analytics-card-label">Avg Profile Completion</div>
          </div>
          <div className="analytics-card">
            <div className="analytics-card-val" style={{ color: '#f97316' }}>{eventStats?.activity_breakdown?.length || 0}</div>
            <div className="analytics-card-label">Active Sub-Events</div>
          </div>
        </div>

        {/* Sub-Events Management */}
        <div style={{ background: 'var(--glass-bg)', padding: '1.5rem', borderRadius: 20, border: '1px solid var(--glass-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Sub-Events & Activities</h3>
            <button className="btn-profile btn-profile-primary" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }} onClick={() => setShowCreateAct(true)}>+ Add Sub-Event</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
            {eventStats?.activity_breakdown?.map((act, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', padding: '1.2rem', borderRadius: 14 }}>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-main)' }}>{act.activity_name}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.4rem' }}>{act.count} Registered Students</div>
              </div>
            ))}
          </div>
        </div>

        {/* Joined Participants Directory */}
        <div id="events-participants-section" style={{ background: 'var(--glass-bg)', padding: '1.5rem', borderRadius: 20, border: '1px solid var(--glass-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                👥 Joined Participants Directory
                <span style={{ fontSize: '0.8rem', background: 'var(--accent-color, #8b5cf6)', color: 'white', padding: '2px 10px', borderRadius: 12, fontWeight: 700 }}>
                  {filteredParticipants.length} Attendee{filteredParticipants.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0.3rem 0 0 0' }}>
                Registered students and participants for {curEvent?.name || 'this event'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn-profile btn-profile-secondary" 
                style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem' }}
                onClick={() => fetchAdminParticipants(selectedEventId)}
              >
                <RefreshCw size={14} style={{ marginRight: 6 }} /> Refresh
              </button>
            </div>
          </div>

          {/* Search Input */}
          <div style={{ marginBottom: '1rem' }}>
            <input 
              className="profile-input" 
              placeholder="Search participants by name, roll number, department, email, or school..." 
              value={adminParticipantsSearch} 
              onChange={e => setAdminParticipantsSearch(e.target.value)} 
            />
          </div>

          {/* Participant Rows */}
          {loadingAdminParticipants ? (
            <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
              Loading participants...
            </div>
          ) : filteredParticipants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
              {adminParticipantsSearch ? `No participants found matching "${adminParticipantsSearch}"` : 'No participants registered for this event yet.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '480px', overflowY: 'auto', paddingRight: '4px' }}>
              {filteredParticipants.map((p, i) => (
                <div 
                  key={p.email || i} 
                  style={{ 
                    padding: '0.85rem 1.15rem', 
                    background: 'rgba(255,255,255,0.03)', 
                    borderRadius: 12, 
                    border: '1px solid var(--glass-border)', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    flexWrap: 'wrap', 
                    gap: '0.6rem' 
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-main)' }}>{p.name || 'Unnamed Participant'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {p.rollNo && <span style={{ fontWeight: 700, color: 'var(--accent-color, #8b5cf6)', marginRight: 6 }}>{p.rollNo}</span>}
                      {p.department && <span>• {p.department} </span>}
                      {p.year && <span>({p.year}) </span>}
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>• {p.email}</span>
                    </div>
                  </div>
                  {p.school_name && (
                    <span style={{ fontSize: '0.75rem', color: '#8b5cf6', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', padding: '0.3rem 0.75rem', borderRadius: 10, fontWeight: 600 }}>
                      🎓 {p.school_name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Event Admin Assignment & Announcement */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        {/* Assign Event Admin */}
        <div style={{ background: 'var(--glass-bg)', padding: '1.5rem', borderRadius: 20, border: '1px solid var(--glass-border)' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Assign Event Admin</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Grant event management permissions to a faculty or student email.</p>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <input className="profile-input" placeholder="e.g. faculty@vnrvjiet.in" value={assignAdminEmail} onChange={e => setAssignAdminEmail(e.target.value)} />
            <button className="btn-profile btn-profile-primary" onClick={async () => {
              if (!assignAdminEmail) return;
              try {
                const res = await fetch(`${API_URL}/events-admin/assign`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ event_id: selectedEventId, user_email: assignAdminEmail, admin_email: user.email })
                });
                const d = await res.json();
                if (d.success) { alert('Event Admin assigned successfully!'); setAssignAdminEmail(''); }
                else alert(d.error || 'Failed to assign admin');
              } catch(e) { alert('Error assigning admin'); }
            }}>Assign</button>
          </div>
        </div>

        {/* Broadcast Announcement */}
        <div style={{ background: 'var(--glass-bg)', padding: '1.5rem', borderRadius: 20, border: '1px solid var(--glass-border)' }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Push Announcement</h3>
          <input className="profile-input" placeholder="Announcement Title" value={eventAnnouncement.title} onChange={e => setEventAnnouncement({ ...eventAnnouncement, title: e.target.value })} style={{ marginBottom: '0.75rem' }} />
          <textarea className="profile-input" placeholder="Message content..." rows={2} value={eventAnnouncement.message} onChange={e => setEventAnnouncement({ ...eventAnnouncement, message: e.target.value })} style={{ marginBottom: '0.75rem' }} />
          <button className="btn-profile btn-profile-primary" onClick={async () => {
            if (!eventAnnouncement.title || !eventAnnouncement.message) return;
            try {
              const res = await fetch(`${API_URL}/events-admin/${selectedEventId}/announcements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...eventAnnouncement, created_by: user.email, admin_email: user.email })
              });
              const d = await res.json();
              if (d.success) { alert('Broadcast push notification sent!'); setEventAnnouncement({ title: '', message: '' }); }
              else alert('Failed to send broadcast');
            } catch(e) { alert('Error sending broadcast'); }
          }}>🚀 Send Event Push</button>
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateEvent && (
        <div className="stats-modal-overlay">
          <div className="stats-modal" style={{ padding: '2rem', maxWidth: 540 }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Create New Event</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input className="profile-input" placeholder="Event Name (e.g. Convergence 2K26)" value={newEvent.name} onChange={e => setNewEvent({ ...newEvent, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })} />
              <input className="profile-input" placeholder="Slug (e.g. convergence2k26)" value={newEvent.slug} onChange={e => setNewEvent({ ...newEvent, slug: e.target.value })} />
              
              {/* Cover Image Input */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input 
                  className="profile-input" 
                  placeholder="Cover Image URL (https://...)" 
                  value={newEvent.banner?.startsWith('data:') ? '' : newEvent.banner} 
                  onChange={e => setNewEvent({ ...newEvent, banner: e.target.value })} 
                  style={{ flex: 1 }}
                />
                <label className="btn-profile btn-profile-secondary" style={{ cursor: 'pointer', padding: '0.5rem 0.8rem', fontSize: '0.8rem' }}>
                  Upload
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => setNewEvent(prev => ({ ...prev, banner: reader.result }));
                      reader.readAsDataURL(file);
                    }} 
                    style={{ display: 'none' }} 
                  />
                </label>
              </div>
              {newEvent.banner && (
                <div style={{ height: 90, borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <img src={newEvent.banner} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}

              <textarea className="profile-input" placeholder="Description" rows={3} value={newEvent.description} onChange={e => setNewEvent({ ...newEvent, description: e.target.value })} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <input type="date" className="profile-input" value={newEvent.start_date} onChange={e => setNewEvent({ ...newEvent, start_date: e.target.value })} />
                <input type="date" className="profile-input" value={newEvent.end_date} onChange={e => setNewEvent({ ...newEvent, end_date: e.target.value })} />
              </div>
              <input className="profile-input" placeholder="Venue (e.g. VNRVJIET Campus)" value={newEvent.venue} onChange={e => setNewEvent({ ...newEvent, venue: e.target.value })} />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn-profile btn-profile-secondary" style={{ flex: 1 }} onClick={() => setShowCreateEvent(false)}>Cancel</button>
                <button className="btn-profile btn-profile-primary" style={{ flex: 1 }} onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/events-admin/create`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ ...newEvent, created_by: user.email, admin_email: user.email })
                    });
                    const d = await res.json();
                    if (d.success) {
                      alert('Event created successfully!');
                      setShowCreateEvent(false);
                      setNewEvent({ name: '', slug: '', description: '', banner: '', start_date: '', end_date: '', venue: '', mode: 'In-Person', organization: 'VNRVJIET' });
                      fetchAdminEvents();
                    } else alert(d.error || 'Failed to create event');
                  } catch(e) { alert('Error creating event'); }
                }}>Create Event</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin People-to-People Connections Modal */}
      {showAdminConnectionsModal && (
        <div className="stats-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowAdminConnectionsModal(false); }}>
          <div className="stats-modal" style={{ padding: '2rem', maxWidth: 700, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-main)' }}>🤝 Event Connections Directory</h3>
              <button onClick={() => setShowAdminConnectionsModal(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
            </div>
            <input 
              className="profile-input" 
              placeholder="Filter by name, email, or roll number..." 
              value={adminConnectionsSearch} 
              onChange={e => setAdminConnectionsSearch(e.target.value)} 
              style={{ marginBottom: '1rem' }} 
            />

            {loadingAdminConnections ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading connections...</div>
            ) : adminConnections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No connections found for this event.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {adminConnections
                  .filter(c => {
                    if (!adminConnectionsSearch) return true;
                    const q = adminConnectionsSearch.toLowerCase();
                    return c.requester.name?.toLowerCase().includes(q) ||
                           c.requester.email?.toLowerCase().includes(q) ||
                           c.requester.rollNo?.toLowerCase().includes(q) ||
                           c.receiver.name?.toLowerCase().includes(q) ||
                           c.receiver.email?.toLowerCase().includes(q) ||
                           c.receiver.rollNo?.toLowerCase().includes(q);
                  })
                  .map(conn => (
                    <div key={conn.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '1rem', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-main)' }}>{conn.requester.name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{conn.requester.rollNo} • {conn.requester.department}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{conn.requester.email}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 8, background: conn.status === 'accepted' ? 'rgba(34,197,94,0.15)' : 'rgba(249,115,22,0.15)', color: conn.status === 'accepted' ? '#22c55e' : '#f97316' }}>
                          {conn.status === 'accepted' ? '🤝 Connected' : '⏳ Pending'}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-main)' }}>{conn.receiver.name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{conn.receiver.rollNo} • {conn.receiver.department}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{conn.receiver.email}</div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Sub-Event Modal */}
      {showCreateAct && (
        <div className="stats-modal-overlay">
          <div className="stats-modal" style={{ padding: '2rem' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-main)' }}>Add Sub-Event / Activity</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <input className="profile-input" placeholder="Activity Name (e.g. AI Hackathon)" value={newAct.name} onChange={e => setNewAct({ ...newAct, name: e.target.value })} />
              <select className="profile-input" value={newAct.category} onChange={e => setNewAct({ ...newAct, category: e.target.value })}>
                <option value="Hackathon">Hackathon</option>
                <option value="CTF">CTF</option>
                <option value="Contest">Contest</option>
                <option value="Workshop">Workshop</option>
                <option value="Paper Presentation">Paper Presentation</option>
              </select>
              <textarea className="profile-input" placeholder="Description & Rules" rows={2} value={newAct.description} onChange={e => setNewAct({ ...newAct, description: e.target.value })} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <input type="date" className="profile-input" value={newAct.date} onChange={e => setNewAct({ ...newAct, date: e.target.value })} />
                <input className="profile-input" placeholder="Venue" value={newAct.venue} onChange={e => setNewAct({ ...newAct, venue: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn-profile btn-profile-secondary" style={{ flex: 1 }} onClick={() => setShowCreateAct(false)}>Cancel</button>
                <button className="btn-profile btn-profile-primary" style={{ flex: 1 }} onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/events-admin/${selectedEventId}/activities/create`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ ...newAct, admin_email: user.email })
                    });
                    const d = await res.json();
                    if (d.success) {
                      alert('Sub-event created successfully!');
                      setShowCreateAct(false);
                      fetchEventStats();
                    } else alert(d.error || 'Failed to create activity');
                  } catch(e) { alert('Error creating activity'); }
                }}>Add Activity</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

  const sidebarTabs = [
    { id: 'analytics', label: 'Dashboard Stats', icon: <BarChart2 size={20} /> },
    { id: 'test-faculty', label: 'Faculty Test Logins', icon: <FlaskConical size={20} /> },
    { id: 'events', label: 'Events & Networking', icon: <Sparkles size={20} /> },
    { id: 'students', label: 'Students Directory', icon: <Users size={20} /> },
    { id: 'faculty', label: 'Faculty Directory', icon: <Briefcase size={20} /> },
    { id: 'formation', label: 'Team Formation', icon: <UserCheck size={20} /> },
    { id: 'explore', label: 'Explore Map', icon: <Search size={20} /> },
    { id: 'faculty-schedule', label: 'Faculty Timetable', icon: <Calendar size={20} /> },
    { id: 'attendance', label: 'Attendance Requests', icon: <CheckCircle size={20} /> },
    { id: 'emails', label: 'Email Telemetry', icon: <Mail size={20} /> },
    { id: 'broadcast', label: 'Broadcasting Hub', icon: <Radio size={20} /> },
    { id: 'feedback', label: 'User Feedback', icon: <MessageSquare size={20} /> },
  ];

  return (
    <div className="admin-layout">
      {/* ── Left Sidebar Navigation ── */}
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="admin-sidebar-header">
          {!sidebarCollapsed && (
            <span style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
              VJLINK Admin
            </span>
          )}
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <nav className="admin-sidebar-nav">
          {sidebarTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id)}
              className={`admin-nav-item ${currentTab === tab.id ? 'active' : ''}`}
              title={sidebarCollapsed ? tab.label : ""}
            >
              {tab.icon}
              {!sidebarCollapsed && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>
      </aside>

      {/* ── Right Content Area ── */}
      <main className="admin-main">
        {/* Topbar */}
        <header className="admin-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h1 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.4rem', fontWeight: 800 }}>
              {sidebarTabs.find(t => t.id === currentTab)?.label || "Admin Portal"}
            </h1>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={() => navigate(user?.role === 'faculty' ? '/faculty/home' : '/home')}
              title="Return to Home"
              style={{
                width: 42, height: 42, borderRadius: '12px',
                border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'all 0.2s'
              }}
            >
              <Home size={18} />
            </button>
            <button
              onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }}
              title="Logout"
              style={{
                width: 42, height: 42, borderRadius: '12px',
                border: 'none', background: '#ef4444',
                color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(239,68,68,0.35)', transition: 'all 0.2s'
              }}
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Content Box */}
        <div className="admin-content-area">
          {currentTab === 'analytics' ? renderAnalyticsTab() : currentTab === 'test-faculty' ? renderTestFacultyTab() : currentTab === 'events' ? renderEventsTab() : currentTab === 'formation' ? renderFormationTab() : currentTab === 'feedback' ? renderFeedbackTab() : currentTab === 'explore' ? (
            <div style={{ height: 'calc(100vh - 180px)', background: 'var(--glass-bg)', borderRadius: 20, border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
              <iframe
                src="/explore"
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Explore Map"
              />
            </div>
          ) : currentTab === 'faculty-schedule' ? (
            <AttendanceAdmin inlineMode />
          ) : currentTab === 'attendance' ? (
            /* ====== ATTENDANCE MONITORING TAB ====== */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.25rem' }}>
                {[
                  { label: 'Coordinator Pending', value: attSummary.coordinator_pending, color: '#6366F1', icon: <Clock size={20} /> },
                  { label: 'HOD Pending', value: attSummary.hod_pending, color: '#F59E0B', icon: <AlertCircle size={20} /> },
                  { label: 'Faculty Pending', value: attSummary.faculty_pending, color: '#3B82F6', icon: <Send size={20} /> },
                  { label: 'Completed', value: attSummary.completed, color: '#10B981', icon: <CheckCircle size={20} /> },
                  { label: 'Rejected', value: attSummary.rejected, color: '#EF4444', icon: <AlertCircle size={20} /> },
                  { label: 'Today Total', value: attSummary.today_total, color: '#8B5CF6', icon: <Mail size={20} /> },
                ].map(card => (
                  <div key={card.label} style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${card.color}15`, color: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{card.icon}</div>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: card.color, lineHeight: 1.2 }}>{card.value}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{card.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', padding: 18, borderRadius: 16 }}>
                <div style={{ minWidth: 160 }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Status</label>
                  <select value={attFilter.status} onChange={e => setAttFilter(f => ({ ...f, status: e.target.value }))} style={{ padding: '0.6rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', width: '100%' }}>
                    <option value="">All</option>
                    <option value="pending_coordinator">Coordinator Pending</option>
                    <option value="pending_hod">HOD Pending</option>
                    <option value="pending_faculty">Faculty Pending</option>
                    <option value="attendance_given">Completed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div style={{ minWidth: 160 }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Date</label>
                  <input type="date" value={attFilter.date} onChange={e => setAttFilter(f => ({ ...f, date: e.target.value }))} style={{ padding: '0.6rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', width: '100%' }} />
                </div>
                <div style={{ minWidth: 160 }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Department</label>
                  <input placeholder="e.g. CSE" value={attFilter.department} onChange={e => setAttFilter(f => ({ ...f, department: e.target.value }))} style={{ padding: '0.6rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', width: '100%' }} />
                </div>
                <button onClick={() => { fetchAttSummary(); fetchAttRequests(); }} style={{ padding: '0.6rem 1.4rem', borderRadius: 8, background: 'var(--accent-color)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8, height: 'fit-content' }}>
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>

              {/* Requests Table */}
              <div style={{ overflowX: 'auto', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: 12 }}>
                {attLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading requests...</div>
                ) : attRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No requests found</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        {['#', 'Roll No', 'Name', 'Date', 'Type', 'Reason', 'Status', 'Coordinator', 'HOD'].map(h => (
                          <th key={h} style={{ padding: '12px 10px', textAlign: 'left', color: 'var(--text-main)', fontSize: '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {attRequests.map((r, idx) => {
                        const statusColors = {
                          pending_coordinator: { bg: '#6366F120', color: '#6366F1', label: '⏳ Coord Pending' },
                          pending_hod: { bg: '#F59E0B20', color: '#F59E0B', label: '⏳ HOD Pending' },
                          pending_faculty: { bg: '#3B82F620', color: '#3B82F6', label: '📩 Faculty Pending' },
                          attendance_given: { bg: '#10B98120', color: '#10B981', label: '✅ Completed' },
                          rejected: { bg: '#EF444420', color: '#EF4444', label: '❌ Rejected' },
                        };
                        const st = statusColors[r.status] || { bg: '#gray', color: 'gray', label: r.status };
                        const periods = Array.isArray(r.periods) ? r.periods.join(', ') : (r.type === 'full_day' ? 'Full Day' : '—');
                        return (
                          <tr key={r.id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '10px 8px' }}>{r.id}</td>
                            <td style={{ padding: '10px 8px' }}>{r.student?.rollNo || '—'}</td>
                            <td style={{ padding: '10px 8px' }}>{r.student?.name || '—'}</td>
                            <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{r.date}</td>
                            <td style={{ padding: '10px 8px' }}>{periods}</td>
                            <td style={{ padding: '10px 8px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
                            <td style={{ padding: '10px 8px' }}>
                              <span style={{ background: st.bg, color: st.color, padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{st.label}</span>
                            </td>
                            <td style={{ padding: '10px 8px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.coordinator_status === 'approved' ? '✅' : r.coordinator_status === 'rejected' ? '❌' : '⏳'}</td>
                            <td style={{ padding: '10px 8px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.hod_status === 'approved' ? '✅' : r.hod_status === 'rejected' ? '❌' : '⏳'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : currentTab === 'emails' ? (
            /* ====== EMAIL LOGS TAB ====== */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.25rem' }}>
                {[
                  { label: 'Total Sent', value: emailStats.total.sent, color: '#10B981' },
                  { label: 'Total Failed', value: emailStats.total.failed, color: '#EF4444' },
                  { label: 'Today Sent', value: emailStats.today.sent, color: '#3B82F6' },
                  { label: 'Today Failed', value: emailStats.today.failed, color: '#F59E0B' },
                ].map(card => (
                  <div key={card.label} style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: '1.25rem', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: card.color }}>{card.value}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{card.label}</div>
                  </div>
                ))}
              </div>

              {/* Today by type breakdown */}
              {emailStats.todayByType.length > 0 && (
                <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                  {emailStats.todayByType.map(t => (
                    <span key={t.email_type} style={{ background: 'rgba(99,102,241,0.1)', color: '#6366F1', padding: '6px 14px', borderRadius: 20, fontSize: '0.85rem', fontWeight: 'bold' }}>
                      {t.email_type}: {t.count}
                    </span>
                  ))}
                </div>
              )}

              {/* Filters */}
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', padding: 18, borderRadius: 16 }}>
                <div style={{ minWidth: 140 }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Type</label>
                  <select value={emailFilter.type} onChange={e => setEmailFilter(f => ({ ...f, type: e.target.value }))} style={{ padding: '0.6rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', width: '100%' }}>
                    <option value="">All</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="hod">HOD</option>
                    <option value="faculty">Faculty</option>
                  </select>
                </div>
                <div style={{ minWidth: 140 }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Status</label>
                  <select value={emailFilter.status} onChange={e => setEmailFilter(f => ({ ...f, status: e.target.value }))} style={{ padding: '0.6rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', width: '100%' }}>
                    <option value="">All</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div style={{ minWidth: 160 }}>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Date</label>
                  <input type="date" value={emailFilter.date} onChange={e => setEmailFilter(f => ({ ...f, date: e.target.value }))} style={{ padding: '0.6rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', width: '100%' }} />
                </div>
                <button onClick={() => { fetchEmailLogs(); fetchEmailStats(); }} style={{ padding: '0.6rem 1.4rem', borderRadius: 8, background: 'var(--accent-color)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8, height: 'fit-content' }}>
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>

              {/* Email Logs Table */}
              <div style={{ overflowX: 'auto', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: 12 }}>
                {emailLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading email logs...</div>
                ) : emailLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No email logs found</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        {['Time', 'Type', 'To', 'Name', 'Dept/Sec', 'Students', 'Status', 'Action'].map(h => (
                          <th key={h} style={{ padding: '12px 10px', textAlign: 'left', color: 'var(--text-main)', fontSize: '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogs.map((log) => {
                        const typeColors = { coordinator: '#6366F1', hod: '#F59E0B', faculty: '#10B981' };
                        const tc = typeColors[log.email_type] || '#6366F1';
                        return (
                          <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '10px 8px', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{new Date(log.created_at).toLocaleString()}</td>
                            <td style={{ padding: '10px 8px' }}>
                              <span style={{ background: `${tc}20`, color: tc, padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>{log.email_type}</span>
                            </td>
                            <td style={{ padding: '10px 8px', fontSize: '0.78rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.recipient_email}</td>
                            <td style={{ padding: '10px 8px', fontSize: '0.82rem' }}>{log.recipient_name || '—'}</td>
                            <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: 'var(--text-muted)' }}>{[log.department, log.section].filter(Boolean).join(' - ') || '—'}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold' }}>{log.student_count}</td>
                            <td style={{ padding: '10px 8px' }}>
                              <span style={{ background: log.status === 'sent' ? '#10B98120' : '#EF444420', color: log.status === 'sent' ? '#10B981' : '#EF4444', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 'bold' }}>
                                {log.status === 'sent' ? '✅ Sent' : '❌ Failed'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 8px' }}>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  onClick={() => handleRetrigger(log.id)}
                                  disabled={retriggerLoading[log.id]}
                                  style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#6366F120', color: '#6366F1', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                                >
                                  <RotateCcw size={12} /> {retriggerLoading[log.id] ? '...' : 'Resend'}
                                </button>
                                {log.batch_id && !log.batch_id.startsWith('retrigger') && !log.batch_id.startsWith('batch-retrigger') && (
                                  <button
                                    onClick={() => handleRetriggerBatch(log.batch_id)}
                                    disabled={retriggerLoading[`batch-${log.batch_id}`]}
                                    style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#8B5CF620', color: '#8B5CF6', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                                  >
                                    <RotateCcw size={12} /> {retriggerLoading[`batch-${log.batch_id}`] ? '...' : 'All'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : currentTab === 'broadcast' ? (
            /* ====== BROADCAST TAB ====== */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1.25rem' }}>
                {[
                  { label: 'Total Broadcasts', value: broadcastStats.totalBroadcasts, color: '#8B5CF6', icon: <Radio size={20} /> },
                  { label: 'Today Broadcasts', value: broadcastStats.todayBroadcasts, color: '#EC4899', icon: <Send size={20} /> },
                  { label: 'Total Recipients', value: broadcastStats.totalRecipients, color: '#3B82F6', icon: <Users size={20} /> },
                  { label: 'Today Recipients', value: broadcastStats.todayRecipients, color: '#10B981', icon: <Mail size={20} /> },
                ].map(card => (
                  <div key={card.label} style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1.25rem', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${card.color}15`, color: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{card.icon}</div>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: card.color, lineHeight: 1.2 }}>{card.value}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{card.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Send Broadcast */}
              <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: '1.75rem' }}>
                <h3 style={{ margin: '0 0 1rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.15rem' }}><Send size={18} /> Send Broadcast</h3>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600 }}>Audience</label>
                    <select value={broadcastAudience} onChange={e => setBroadcastAudience(e.target.value)} style={{ padding: '0.6rem 1rem', borderRadius: 8, border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', minWidth: 200, fontSize: '0.95rem' }}>
                      <option value="all">🌍 All (Faculty + Students)</option>
                      <option value="faculty">👨‍🏫 Faculty Only</option>
                      <option value="students">🎓 Students Only</option>
                    </select>
                  </div>
                  <button
                    onClick={sendBroadcast}
                    disabled={broadcastSending}
                    style={{ padding: '0.65rem 1.8rem', borderRadius: 8, background: broadcastSending ? '#6b7280' : 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: 'white', border: 'none', cursor: broadcastSending ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 14px rgba(79,70,229,0.3)' }}
                  >
                    <Send size={16} /> {broadcastSending ? 'Sending...' : 'Send VJLINK Announcement'}
                  </button>
                  <button onClick={() => { fetchBroadcastLogs(); fetchBroadcastStats(); }} style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'var(--accent-color)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <RefreshCw size={14} /> Refresh
                  </button>
                </div>
              </div>

              {/* Broadcast History */}
              <div style={{ overflowX: 'auto', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 16, padding: 12 }}>
                <h3 style={{ margin: '0.8rem 0.5rem 1rem', color: 'var(--text-main)', fontSize: '1.15rem' }}>📜 Broadcast History</h3>
                {broadcastLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading...</div>
                ) : broadcastLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No broadcasts sent yet</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        {['Time', 'Subject', 'Audience', 'Faculty', 'Students', 'Total', 'Status', ''].map(h => (
                          <th key={h} style={{ padding: '12px 10px', textAlign: 'left', color: 'var(--text-main)', fontSize: '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {broadcastLogs.map(log => {
                        const audienceColors = { all: '#8B5CF6', faculty: '#F59E0B', students: '#3B82F6' };
                        const ac = audienceColors[log.audience] || '#6366F1';
                        const isExpanded = expandedBroadcast === log.id;
                        return (
                          <React.Fragment key={log.id}>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer' }} onClick={() => setExpandedBroadcast(isExpanded ? null : log.id)}>
                              <td style={{ padding: '10px 8px', whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{new Date(log.created_at).toLocaleString()}</td>
                              <td style={{ padding: '10px 8px', fontSize: '0.85rem', fontWeight: 'bold', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.subject}</td>
                              <td style={{ padding: '10px 8px' }}>
                                <span style={{ background: `${ac}20`, color: ac, padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>{log.audience}</span>
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                <span style={{ background: '#F59E0B20', color: '#F59E0B', padding: '2px 8px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 'bold' }}>
                                  <Briefcase size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />{log.faculty_count}
                                </span>
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                <span style={{ background: '#3B82F620', color: '#3B82F6', padding: '2px 8px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 'bold' }}>
                                  <Users size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />{log.student_count}
                                </span>
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold', fontSize: '1rem' }}>{log.total_recipients}</td>
                              <td style={{ padding: '10px 8px' }}>
                                <span style={{ background: log.status === 'sent' ? '#10B98120' : '#EF444420', color: log.status === 'sent' ? '#10B981' : '#EF4444', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 'bold' }}>
                                  {log.status === 'sent' ? '✅ Sent' : '❌ Failed'}
                                </span>
                              </td>
                              <td style={{ padding: '10px 8px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{isExpanded ? '▲' : '▼'}</td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={8} style={{ padding: '12px 16px', background: 'rgba(99,102,241,0.05)' }}>
                                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', maxHeight: 200, overflowY: 'auto' }}>
                                    <strong>Recipients ({log.recipient_emails?.length || 0}):</strong>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                      {(log.recipient_emails || []).map((email, i) => (
                                        <span key={i} style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', padding: '3px 8px', borderRadius: 6, fontSize: '0.75rem' }}>{email}</span>
                                      ))}
                                    </div>
                                    {log.message_id && <p style={{ marginTop: 8 }}><strong>Message ID:</strong> {log.message_id}</p>}
                                    {log.error_message && <p style={{ color: '#EF4444', marginTop: 8 }}><strong>Error:</strong> {log.error_message}</p>}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : renderUserTable()}
        </div>

        {/* Modal overlays */}
        {renderUsersListModal()}

        {showCollaborationModal && selectedTaskForCollaboration && (
          <CollaborationModal
            taskId={selectedTaskForCollaboration.id}
            taskName={selectedTaskForCollaboration.name}
            requesterEmail={user?.email}
            onClose={() => {
              setShowCollaborationModal(false);
              setSelectedTaskForCollaboration(null);
            }}
          />
        )}

        {/* ZIP Export Progress Modal */}
        {zipProgress && (
          <div className="stats-modal-overlay">
            <div className="stats-modal" style={{ background: '#12131C', width: '90%', maxWidth: 700, height: '60vh' }}>
              <div className="stats-modal-header">
                <h3 style={{ color: 'white', margin: 0 }}>📦 Exporting ZIP...</h3>
                <button 
                  onClick={() => setZipProgress(null)}
                  style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 20 }}
                >✕</button>
              </div>
              
              <div className="stats-modal-content" style={{ background: '#07080E', border: '1px solid var(--glass-border)', borderRadius: 16 }}>
                {zipProgress.logs.map((log, i) => (
                  <div key={i} style={{ 
                    marginBottom: 6, 
                    fontFamily: 'monospace',
                    fontSize: 13,
                    color: log.includes('✅') ? '#4ADE80' : log.includes('⚠️') ? '#FBBF24' : log.includes('❌') ? '#EF4444' : '#A3A3A3' 
                  }}>
                    {log}
                  </div>
                ))}
                <div ref={zipLogsEndRef} />
              </div>
            </div>
          </div>
        )}
        <ExcelRoomUploadModal
          isOpen={showExcelModal}
          onClose={() => setShowExcelModal(false)}
          onSuccess={fetchFormationTasks}
          creatorEmail={JSON.parse(localStorage.getItem('vjconnect_user') || '{}').email || ''}
        />
      </main>
    </div>
  );
}

export default Admin;
